import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Journal() {
  useSeo({
    title: "Shaji Nada | Journal — Essays, News & Cultural Milestones",
    description:
      "Long-form essays and news from Shaji Nada on independent South Asian music, film production, and cultural infrastructure.",
    canonicalPath: "/journal",
  });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "essay" | "news"

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/posts`)
      .then((r) => mounted && setPosts(r.data))
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = filter === "all" ? posts : posts.filter((p) => (p.category || "essay") === filter);

  const filters = [
    { key: "all", label: "All" },
    { key: "essay", label: "Essays" },
    { key: "news", label: "News" },
  ];

  return (
    <div data-testid="journal-page" className="pt-32 md:pt-40 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="overline mb-6">Journal</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.98] max-w-5xl" data-testid="journal-h1">
          Essays, news, and <span className="italic text-[var(--accent)]">cultural infrastructure.</span>
        </h1>
      </section>

      {/* Filter tabs */}
      <section className="mt-14 md:mt-20 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-8 border-b border-white/10 pb-4" data-testid="journal-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`journal-filter-${f.key}`}
              className={`text-xs uppercase tracking-[0.22em] font-medium transition-colors duration-300 pb-2 ${
                filter === f.key
                  ? "text-[var(--accent)] border-b border-[var(--accent)] -mb-[17px]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-4 max-w-[1400px] mx-auto px-6 md:px-12">
        {loading && <p className="text-white/50 py-16" data-testid="journal-loading">Loading…</p>}
        {error && <p className="text-red-400 py-16" data-testid="journal-error">Could not load posts.</p>}
        {!loading && !error && filtered.length === 0 && (
          <p className="text-white/50 py-16" data-testid="journal-empty">No posts yet in this category.</p>
        )}

        <div className="divide-y divide-white/10">
          {filtered.map((p, idx) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: idx * 0.07 }}
              className="py-12 md:py-16 grid md:grid-cols-12 gap-8 group"
              data-testid={`journal-post-${idx}`}
            >
              <Link to={`/journal/${p.slug}`} className="md:col-span-5 block overflow-hidden bg-[#1A1A1A]">
                <div className="aspect-[16/10]">
                  <img
                    src={p.hero_image}
                    alt={`${p.title} — Shaji Nada`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  />
                </div>
              </Link>
              <div className="md:col-span-7 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs uppercase tracking-[0.22em] text-white/50 flex-wrap">
                  <span className="text-[var(--accent)]">{p.category === "news" ? "News" : "Essay"}</span>
                  <span>·</span>
                  <span>{formatDate(p.published_at)}</span>
                  <span>·</span>
                  <span>{p.read_minutes} min read</span>
                </div>
                <h2 className="mt-4 font-serif-display text-3xl md:text-5xl text-white leading-[1.05]">
                  <Link
                    to={`/journal/${p.slug}`}
                    className="hover:text-[var(--accent)] transition-colors"
                    data-testid={`journal-title-${idx}`}
                  >
                    {p.title}
                  </Link>
                </h2>
                <p className="mt-5 text-white/70 font-light leading-relaxed max-w-2xl">{p.excerpt}</p>
                <Link
                  to={`/journal/${p.slug}`}
                  data-testid={`journal-read-${idx}`}
                  className="mt-6 inline-flex items-center gap-2 text-[var(--accent)] uppercase tracking-[0.22em] text-xs"
                >
                  Read {p.category === "news" ? "article" : "essay"} <ArrowUpRight size={14} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
